import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, ChevronDown } from "lucide-react";
import LanguageSwitcher from "../UI/LanguageSwitcher";
import AuthModal from "../Auth/AuthModal";
import { useAuth } from "../../contexts/AuthContext";

const Header = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVillasMenuOpen, setIsVillasMenuOpen] = useState(false);
  const [isMobileVillasOpen, setIsMobileVillasOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isImageError, setIsImageError] = useState(false);

  const bgImageUrl = "/assets/icons/top-img-bg.png";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsVillasMenuOpen(false);
    setIsMobileVillasOpen(false);
  }, [location.pathname]);

  const villaSubLinks = [
    { name: t("nav.villaNajat"), href: "/villa-najat" },
    { name: t("nav.villaNadia"), href: "/villa-nadia" },
  ];

  const navigation = [
    { name: t("nav.home"), href: "/" },
    { name: t("nav.about"), href: "/about" },
    {
      name: t("nav.ourVillas"),
      href: "/our-villas",
      children: villaSubLinks,
    },
    { name: t("nav.services"), href: "/services" },
    { name: t("nav.gastronomy"), href: "/gastronomy" },
    { name: t("nav.reviews"), href: "/reviews" },
    { name: t("nav.gallery"), href: "/gallery" },
    { name: t("nav.contact"), href: "/contact" },
  ];

  const isVillasActive = () =>
    location.pathname.startsWith("/our-villas") ||
    location.pathname.startsWith("/villa-najat") ||
    location.pathname.startsWith("/villa-nadia");

  const isActive = (path) => {
    return path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
  };

  return (
    <header className={`shadow-soft sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white' : 'bg-white'}`}>
      {/* 🔍 Hidden image to check load status */}
      <img
        src={bgImageUrl}
        alt=""
        style={{ display: "none" }}
        onLoad={() => setIsImageLoaded(true)}
        onError={() => setIsImageError(true)}
      />

      {/* 📱 Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/*  Top Bar */}
      <div
        style={{
          background: !isScrolled
            ? isImageLoaded && !isImageError
              ? `url("${bgImageUrl}") center/cover`
              : "#f0f0f0"
            : "white",
          color: "#796d66",
        }}
        className="py-1 sm:py-2 transition-all duration-300"
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center text-xs sm:text-sm">
            <div className="hidden md:flex items-center space-x-4">
              <span className="font-sofia flex flex-row items-center">
                {/* Email Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4 mx-1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <span>{t("contact.email")}</span>
              </span>
              <span className="font-sofia flex flex-row items-center">
                {/* Phone Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4 mx-1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                </svg>
                <span>{t("contact.phone.morocco")}</span>
              </span>
            </div>

            {/* Auth & Language Switch */}
            <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
              {isAuthenticated ? (
                <button
                  onClick={() => navigate("/profile")}
                  className="flex items-center space-x-1 sm:space-x-2 font-sofia hover:text-accent transition-colors duration-200"
                >
                  <User className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline text-xs sm:text-sm">
                    {user?.first_name}
                  </span>
                  <ChevronDown className="h-2 w-2 sm:h-3 sm:w-3" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    setAuthMode("login");
                    setShowAuthModal(true);
                  }}
                  className="font-sofia hover:text-accent transition-colors duration-200 text-xs sm:text-sm"
                >
                  {t("auth.login")}
                </button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* 🔳 Main Header */}
      <div
        className="border-b border-gray-100 transition-all duration-300"
        style={{
          background: isScrolled
            ? isImageLoaded && !isImageError
              ? `url("${bgImageUrl}") center/cover`
              : "#f0f0f0"
            : "white",
          color: "#796d66",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Menu Button (Mobile) */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-md transition-all duration-75 active:scale-95 z-50 hover:text-accent hover:bg-white/10"
              style={{ color: '#796d66' }}
              aria-label={t("nav.toggleMenu")}
            >
              <svg className={`h-6 w-6 transition-transform duration-75 ${isMobileMenuOpen ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logo */}
            <div className="flex-1 flex justify-center lg:justify-start lg:flex-none">
              <Link to="/" className="flex items-center group">
                <img
                  src="/assets/icons/logo-symbol-vb.png"
                  alt="Village Baidane"
                  className="h-8 sm:h-10 lg:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex flex-1 justify-center">
              <div className="flex items-center space-x-1 xl:space-x-3 2xl:space-x-4">
                {navigation.map((item) =>
                  item.children ? (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={() => setIsVillasMenuOpen(true)}
                      onMouseLeave={() => setIsVillasMenuOpen(false)}
                    >
                      <Link
                        to={item.href}
                        className="inline-flex items-center px-1 lg:px-2 xl:px-3 py-2 text-xs lg:text-xs xl:text-sm font-sofia font-medium transition-all duration-300 hover:text-accent whitespace-nowrap"
                        style={{
                          color: isVillasActive() ? "#FF0000" : "#796d66",
                        }}
                      >
                        {item.name}
                        <ChevronDown className="ml-1 h-3 w-3" />
                      </Link>
                      {isVillasMenuOpen && (
                        <div className="absolute left-0 top-full pt-2 z-50">
                          <div className="min-w-[180px] rounded-lg border border-gray-100 bg-white py-2 shadow-lg">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className="block px-4 py-2 text-sm font-sofia text-[#796d66] hover:bg-[#f0f0f0] hover:text-accent transition-colors duration-200"
                                style={{
                                  color: isActive(child.href) ? "#FF0000" : "#796d66",
                                }}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="px-1 lg:px-2 xl:px-3 py-2 text-xs lg:text-xs xl:text-sm font-sofia font-medium transition-all duration-300 hover:text-accent whitespace-nowrap"
                      style={{
                        color: isActive(item.href) ? "#FF0000" : "#796d66",
                      }}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            </nav>

            {/* Book Now Button */}
            <div className="hidden lg:flex items-center">
              <Link
                to="/booking"
                className="bg-[#81736d] text-white px-3 xl:px-4 py-2 rounded-lg text-xs xl:text-sm font-sofia font-medium hover:bg-[#81736d]/90 transition-all duration-300 whitespace-nowrap"
              >
                {t("common.bookNow")}
              </Link>
            </div>

            {/* Mobile Book Now */}
            <div className="lg:hidden">
              <Link
                to="/booking"
                className="bg-[#81736d] text-white px-3 py-2 rounded-lg text-xs font-sofia font-medium hover:bg-[#81736d]/90 transition-all duration-300"
              >
                {t("common.bookNow")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="mobile-menu lg:hidden border-t border-gray-100 shadow-lg transition-all duration-100 ease-out z-50 transform origin-top scale-y-100 opacity-100 visible bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="py-4 space-y-1">
              {navigation.map((item, index) =>
                item.children ? (
                  <div key={item.name}>
                    <button
                      type="button"
                      className={`mobile-menu-item w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-sofia font-medium transition-all duration-75 transform hover:text-accent hover:bg-[#f0f0f0] ${
                        isVillasActive()
                          ? "text-accent border-l-4 border-[#81736d] bg-[#f0f0f0]"
                          : ""
                      }`}
                      style={{
                        color: isVillasActive() ? "#FF0000" : "#796d66",
                        transitionDelay: isMobileMenuOpen ? `${index * 15}ms` : "0ms",
                      }}
                      onClick={() => setIsMobileVillasOpen(!isMobileVillasOpen)}
                    >
                      <span>{item.name}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isMobileVillasOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isMobileVillasOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        <Link
                          to={item.href}
                          className="mobile-menu-item block px-4 py-2 rounded-lg text-sm font-sofia font-medium hover:text-accent hover:bg-[#f0f0f0]"
                          style={{
                            color: isActive(item.href) ? "#FF0000" : "#796d66",
                          }}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {t("ourVillas.viewAllVillas")}
                        </Link>
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="mobile-menu-item block px-4 py-2 rounded-lg text-sm font-sofia font-medium hover:text-accent hover:bg-[#f0f0f0]"
                            style={{
                              color: isActive(child.href) ? "#FF0000" : "#796d66",
                            }}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`mobile-menu-item block px-4 py-3 rounded-lg text-base font-sofia font-medium transition-all duration-75 transform hover:text-accent hover:bg-[#f0f0f0] hover:translate-x-1 ${
                      isActive(item.href) ? "text-accent border-l-4 border-[#81736d] bg-[#f0f0f0]" : ""
                    }`}
                    style={{
                      color: isActive(item.href) ? "#FF0000" : "#796d66",
                      transitionDelay: isMobileMenuOpen ? `${index * 15}ms` : "0ms",
                    }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              )}

              <div className="pt-4 border-t border-gray-200 mt-4">
                <Link
                  to="/booking"
                  className="mobile-menu-item block w-full text-center bg-[#81736d] text-white px-4 py-3 rounded-lg text-base font-sofia font-medium hover:bg-[#81736d]/90 transition-all duration-75 transform"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t("common.bookNow")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </header>
  );
};

export default Header;
